import { useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { Button, Col, Form, Modal, Row, Tab, Tabs } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaEye, FaTrash } from 'react-icons/fa'
import {
  getIncidentById,
  updateIncidentStatus,
  createIncidentComment,
  getIncidentComments,
  getIncidentAuditTrail,
  uploadIncidentAttachment,
  getIncidentAttachments,
  getIncidentAttachmentFile,
  deleteIncidentAttachment,
  assignIncident,
  getAssignableUsers,
  getActiveIncidentCategories
} from '../../../api/IncidentAPI'
import { markEmployeeCommNotificationsAsReadAPI } from '../../../api/NotificationAPI'
import { useAuth } from '../../modules/auth'
import roleContext from '../../context/roleContext'
import { format } from 'date-fns'

interface IncidentDetailProps {
  onTabChange: (tab: string) => void
}

const IncidentDetail = ({ onTabChange }: IncidentDetailProps) => {
  const { currentUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const roleState = useContext(roleContext)

  const [incident, setIncident] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [comments, setComments] = useState<any[]>([])
  const [auditLogs, setAuditLogs] = useState<any[]>([])
  const [incidentCategories, setIncidentCategories] = useState<any[]>([])
  const [attachments, setAttachments] = useState<any[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadLoading, setUploadLoading] = useState(false)
  const [newComment, setNewComment] = useState({
    commentType: 'EMPLOYEE_COMMUNICATION',
    body: '',
  })
  const [commentLoading, setCommentLoading] = useState(false)
  const [visibleToUserIds, setVisibleToUserIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)

  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignData, setAssignData] = useState({
    assignedToId: '',
    remarks: '',
  })
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignableUsers, setAssignableUsers] = useState<any[]>([])
  const fetchAssignableUsers = async () => {
    try {
      const response = await getAssignableUsers(true)

      if (response.statusCode === 200) {
        setAssignableUsers(response.data?.items || [])
      }
    } catch (error) {
      console.error('Failed to fetch assignable users:', error)
    }
  }

  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusData, setStatusData] = useState({
    status: '',
    remarks: '',
  })
  const [statusLoading, setStatusLoading] = useState(false)

  const incidentId = new URLSearchParams(location.search).get('id')

  const normalizedRoleName = (roleState.roleName || '').trim().replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').toUpperCase()
  const isAdmin = ['ADMIN', 'SUPER ADMIN', 'SUPERADMIN'].includes(normalizedRoleName) ||
    roleState.features.some((feature: string) => feature.toLowerCase() === 'admin')
  const isHrRole = normalizedRoleName === 'HR' || normalizedRoleName.startsWith('HR ')
  const canManage = isAdmin || isHrRole || Boolean(currentUser?.isPoshCommitteeMember && incident?.assignedTo?.id === currentUser?.id)
  const canAssign = isAdmin || isHrRole
  const canViewAudit = isAdmin || normalizedRoleName === 'HR HEAD'
  const canDeleteOthersAttachments = ['ADMIN', 'SUPER ADMIN', 'SUPERADMIN', 'HR HEAD'].includes(normalizedRoleName)
  const canDeleteAttachment = (attachment: any) => canDeleteOthersAttachments ||
    attachment.uploadedById === currentUser?.id || attachment.uploadedBy?.id === currentUser?.id

  const canUpdateStatus = isAdmin || normalizedRoleName === 'HR HEAD'
  const isClosed = incident?.status === 'CLOSED'

  useEffect(() => {
    if (incidentId) {
      fetchIncidentDetail()
      fetchComments()
      fetchAttachments()
      fetchAssignableUsers()
      getActiveIncidentCategories().then((response) => {
        if (response.statusCode === 200) setIncidentCategories(response.data || [])
      }).catch(() => undefined)
      // Mark employee communication notifications as read when viewing incident
      markEmployeeCommNotificationsAsReadAPI(currentUser?.id || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId])

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showUserDropdown) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserDropdown])

  const fetchIncidentDetail = async () => {
    if (!incidentId) return
    setLoading(true)
    setError(null)
    try {
      const response = await getIncidentById(incidentId)
      if (response.statusCode === 200) {
        setIncident(response.data)
      } else {
        setError(response.message || 'Failed to fetch incident details')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch incident details')
    } finally {
      setLoading(false)
    }
  }

  const fetchComments = async () => {
    if (!incidentId) return
    try {
      const response = await getIncidentComments(incidentId)
      if (response.statusCode === 200) {
        setComments(response.data || [])
      }
    } catch (err: any) {
      console.error('Error fetching comments:', err)
    }
  }

  const fetchAuditTrail = async () => {
    if (!incidentId) return
    try {
      const response = await getIncidentAuditTrail(incidentId)
      if (response.statusCode === 200) setAuditLogs(response.data || [])
      else setError(response.message || 'Unable to load audit trail')
    } catch (auditError: any) {
      setError(auditError.message || 'Unable to load audit trail')
    }
  }

  const fetchAttachments = async () => {
    if (!incidentId) return
    try {
      const response = await getIncidentAttachments(incidentId)
      if (response.statusCode === 200) {
        setAttachments(response.data || [])
      }
    } catch (err: any) {
      console.error('Error fetching attachments:', err)
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormSubmitted(true)
    
    if (!incidentId || !newComment.body.trim()) return

    setCommentLoading(true)
    try {
      // const response = await createIncidentComment(incidentId, newComment)
      const response = await createIncidentComment(incidentId, {
        ...newComment,
        commentType: canManage ? newComment.commentType : 'EMPLOYEE_COMMUNICATION',
        visibleToUserIds: [],
      })
      if (response.statusCode === 201) {
        // setNewComment({ commentType: 'INTERNAL', body: '' })
        setNewComment({
          commentType: 'EMPLOYEE_COMMUNICATION',
          body: '',
        })
        setVisibleToUserIds([])
        setFormSubmitted(false)
        fetchComments()
      } else {
        alert(response.message || 'Failed to add comment')
      }
    } catch (err: any) {
      alert(err.message || 'Failed to add comment')
    } finally {
      setCommentLoading(false)
    }
  }

  const handleAssign = async () => {
  if (!incidentId || !assignData.assignedToId) return

  setAssignLoading(true)

  try {
    const response = await assignIncident(incidentId, {
      assignedToId: assignData.assignedToId,
      remarks: assignData.remarks || undefined,
    })

    if (response.statusCode === 200) {
      setShowAssignModal(false)

      setAssignData({
        assignedToId: '',
        remarks: '',
      })

      fetchIncidentDetail()
    } else {
      alert(response.message || 'Failed to assign incident')
    }
  } catch (err: any) {
    alert(err.message || 'Failed to assign incident')
  } finally {
    setAssignLoading(false)
  }
}

  const handleStatusUpdate = async () => {
    if (!incidentId || !statusData.status) return

    setStatusLoading(true)
    try {
      const response = await updateIncidentStatus(incidentId, statusData)
      if (response.statusCode === 200) {
        setShowStatusModal(false)
        setStatusData({ status: '', remarks: '' })
        fetchIncidentDetail()
      } else {
        alert(response.message || 'Failed to update status')
      }
    } catch (err: any) {
      alert(err.message || 'Failed to update status')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!incidentId || !e.target.files) return

    const files = Array.from(e.target.files)
    
    // Validate file limit (including already uploaded attachments)
    if (attachments.length + files.length > 5) {
      alert(`You can upload a maximum of 5 files. You already have ${attachments.length} files attached.`)
      return
    }
    
    setSelectedFiles(files)
    
    // Auto-upload all selected files
    setUploadLoading(true)
    
    try {
      const uploadPromises = files.map(file => uploadIncidentAttachment(incidentId, file))
      const responses = await Promise.all(uploadPromises)
      
      const allSuccessful = responses.every(response => response.statusCode === 201)
      
      if (allSuccessful) {
        fetchAttachments()
      } else {
        const failedResponse = responses.find(response => response.statusCode !== 201)
        alert(failedResponse?.message || 'Failed to upload some attachments')
      }
    } catch (err: any) {
      alert(err.message || 'Failed to upload attachments')
    } finally {
      setUploadLoading(false)
      setSelectedFiles([]) // Clear selected files after upload attempt
      // Reset the file input
      e.target.value = ''
    }
  }

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return
    }

    try {
      const response = await deleteIncidentAttachment(attachmentId)
      if (response.statusCode === 202) {
        fetchAttachments()
      } else {
        alert(response.message || 'Failed to delete attachment')
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete attachment')
    }
  }

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      SUBMITTED: 'bg-primary-subtle text-primary',
      ACKNOWLEDGED: 'bg-info-subtle text-info',
      UNDER_REVIEW: 'bg-warning-subtle text-warning',
      PENDING_INFO: 'bg-light text-dark',
      RESOLVED: 'bg-success-subtle text-success',
      CLOSED: 'bg-danger-subtle text-danger',
    }
    return statusColors[status] || 'bg-secondary'
  }

  const getPriorityBadge = (priority: string) => {
    const priorityColors: Record<string, string> = {
      HIGH: 'bg-danger',
      MEDIUM: 'bg-warning',
      LOW: 'bg-success',
    }
    return priorityColors[priority] || 'bg-secondary'
  }

  const getCategoryLabel = (category: string) => {
    return incidentCategories.find((item) => item.code === category)?.name ||
      category.toLowerCase().replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  const formatAuditValue = (value: unknown, entry: any): string => {
    if (value === null || value === undefined || value === '') return '—'
    let parsed: any = value
    if (typeof value === 'string') {
      try { parsed = JSON.parse(value) } catch { parsed = value }
    }
    const formatField = (key: string, fieldValue: any): string => {
      const labels: Record<string, string> = {
        caseId: 'Concern reference',
        categoryCode: 'Category',
        status: 'Status',
        priority: 'Priority',
        fileName: 'Attachment',
        fileSizeKb: 'File size',
        assignedToId: 'Assignment',
        assignedTo: 'Assigned to',
        description: 'Description',
        incidentDate: 'Incident date',
        resolutionSummary: 'Resolution summary',
      }
      if (key === 'categoryCode') return `${labels[key]}: ${getCategoryLabel(String(fieldValue))}`
      if (key === 'status') return `${labels[key]}: ${getStatusLabel(String(fieldValue))}`
      if (key === 'assignedToId') return `${labels[key]}: ${fieldValue ? 'Assigned user' : 'Unassigned'}`
      if (key === 'fileSizeKb') return `${labels[key]}: ${fieldValue} KB`
      const label = labels[key] || key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (letter) => letter.toUpperCase())
      return `${label}: ${String(fieldValue)}`
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if (typeof parsed.fileName === 'string') {
        const size = Number(parsed.fileSizeKb)
        return `${parsed.fileName}${Number.isFinite(size) && size > 0 ? ` (${size} KB)` : ''}`
      }
      return Object.entries(parsed)
        .filter(([key]) => !['id', 'incidentId', 'reporterId', 'actorId'].includes(key))
        .map(([key, fieldValue]) => formatField(key, fieldValue))
        .join(' · ') || 'Details updated'
    }
    if (Array.isArray(parsed)) return parsed.map(String).join(', ')
    if (entry?.section === 'Status' || entry?.eventType === 'STATUS_CHANGED' || entry?.eventType === 'ACKNOWLEDGED') {
      return getStatusLabel(String(parsed))
    }
    if (entry?.section === 'Assignment' && typeof parsed === 'string' && /^[0-9a-f-]{32,36}$/i.test(parsed)) {
      return 'Assigned user'
    }
    return String(parsed)
  }

  const getAuditEventLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      CONCERN_CREATED: 'Concern submitted',
      STATUS_CHANGED: 'Status changed',
      ACKNOWLEDGED: 'Concern acknowledged',
      ASSIGNED: 'Assignment updated',
      FIELDS_EDITED: 'Concern details updated',
      ATTACHMENT_UPLOADED: 'Attachment uploaded',
      ATTACHMENT_REMOVED: 'Attachment removed',
      ATTACHMENT_DOWNLOADED: 'Attachment downloaded',
      ATTACHMENT_VIEWED: 'Attachment viewed',
      COMMENT_ADDED: 'Comment added',
    }
    return labels[eventType] || eventType.replace(/_/g, ' ').toLowerCase().replace(/^./, (letter) => letter.toUpperCase())
  }

  // Status transition matrix
  const statusTransitions: Record<string, string[]> = {
    SUBMITTED: ['ACKNOWLEDGED'],
    ACKNOWLEDGED: ['UNDER_REVIEW', 'PENDING_INFO', 'ESCALATED_ICC', 'CLOSED'],
    UNDER_REVIEW: ['PENDING_INFO', 'ESCALATED_ICC', 'RESOLVED', 'CLOSED'],
    PENDING_INFO: ['UNDER_REVIEW', 'CLOSED'],
    ESCALATED_ICC: ['RESOLVED', 'CLOSED'],
    RESOLVED: ['CLOSED'],
    CLOSED: [],
  }

  const getAllowedNextStatuses = useCallback(() => {
    const currentStatus = incident?.status
    if (!currentStatus) return []
    return statusTransitions[currentStatus] || []
  }, [incident?.status])

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      SUBMITTED: 'Submitted',
      ACKNOWLEDGED: 'Acknowledged',
      UNDER_REVIEW: 'Under Review',
      PENDING_INFO: 'Pending Info',
      ESCALATED_ICC: 'Escalated to ICC',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    }
    return labels[status] || status
  }

  const visibleComments = useMemo(() => {
    return comments.filter(comment => {
      // Employee communication comments are only visible to:
      // 1. The comment author
      // 2. Users in the visibleToUserIds list
      // 3. Admins and Super Admins (handle different name variations)
      // 4. If visibleToUserIds is empty (backward compatibility for old comments), visible to everyone
      if (comment.commentType === 'EMPLOYEE_COMMUNICATION') {
        const isAuthor = comment.author?.id === currentUser?.id
        const isVisibleToUser = comment.visibleToUserIds?.includes(currentUser?.id)
        const isAdmin = canManage
        const hasVisibleUsers = comment.visibleToUserIds && comment.visibleToUserIds.length > 0

        // If no visibleToUserIds specified, show to everyone (backward compatibility)
        if (!hasVisibleUsers) {
          return true
        }

        return isAuthor || isVisibleToUser || isAdmin
      }

      // Internal comments are only visible to:
      // 1. The comment author
      // 2. Users in the visibleToUserIds list
      // 3. Admins and Super Admins (handle different name variations)
      if (comment.commentType === 'INTERNAL') {
        const isAuthor = comment.author?.id === currentUser?.id
        const isVisibleToUser = comment.visibleToUserIds?.includes(currentUser?.id)
        const isAdmin = canManage

        return isAuthor || isVisibleToUser || isAdmin
      }

      return true
    })
  }, [comments, currentUser?.id, canManage])
  const displayedComments = visibleComments.filter(comment => comment.commentType === newComment.commentType)

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (error || !incident) {
    return (
      <div className="alert alert-danger">
        {error || 'Incident not found'}
        <Button variant="link" onClick={() => onTabChange('list')}>
          Back to List
        </Button>
      </div>
    )
  }

  return (
    <div className="incident-detail">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button variant="secondary" onClick={() => onTabChange('list')}>
          ← Back to List
        </Button>
        <div className="d-flex gap-2">
          {canAssign && !isClosed && (
            <Button
              variant="primary"
              onClick={() => {
                fetchAssignableUsers()
                setShowAssignModal(true)
              }}
            >
              Assign
            </Button>
          )}
          {canUpdateStatus && !isClosed && (
            <Button variant="warning" onClick={() => setShowStatusModal(true)}>
              Update Status
            </Button>
          )}
        </div>
      </div>

      <Row>
        <Col md={8}>
          <div className="card mb-3">
            <div className="card-header">
              <h5>Incident Details</h5>
            </div>
            <div className="card-body">
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Case ID:</strong> {incident.caseId}
                  </p>
                  <p>
                    <strong>Category:</strong> {getCategoryLabel(incident.categoryCode)}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${getStatusBadge(incident.status)}`}>
                      {incident.status.replace(/_/g, ' ')}
                    </span>
                  </p>
                  {canManage && <p>
                    <strong>Priority:</strong>{' '}
                    <span className={`badge ${getPriorityBadge(incident.priority)}`}>
                      {incident.priority}
                    </span>
                  </p>}
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Incident Date:</strong>{' '}
                    {incident.incidentDate
                      ? format(new Date(incident.incidentDate), 'MMM dd, yyyy')
                      : '-'}
                  </p>
                  {canManage && <p>
                    <strong>Reporter:</strong>{' '}
                    {incident.reporter
                      ? `${incident.reporter.firstName} ${incident.reporter.lastName}`
                      : 'Anonymous'}
                  </p>}
                  {canManage && <p>
                    <strong>Assigned To:</strong>{' '}
                    {incident.assignedTo
                      ? `${incident.assignedTo.firstName} ${incident.assignedTo.lastName}`
                      : 'Unassigned'}
                  </p>}
                  {canManage && incident.slaDueDate && (
                    <p>
                      <strong>SLA Due Date:</strong>{' '}
                      {format(new Date(incident.slaDueDate), 'MMM dd, yyyy')}
                    </p>
                  )}
                </Col>
              </Row>
              <hr />
              <p>
                <strong>Description:</strong>
              </p>
              <p>{incident.description}</p>
              {incident.peopleInvolved && (
                <p>
                  <strong>People Involved:</strong> {incident.peopleInvolved}
                </p>
              )}
              {incident.locationContext && (
                <p>
                  <strong>Location:</strong> {incident.locationContext}
                </p>
              )}
              {incident.resolutionSummary && (
                <p>
                  <strong>Resolution Summary:</strong> {incident.resolutionSummary}
                </p>
              )}
            </div>
          </div>

          <Tabs defaultActiveKey="comments" className="mb-3">
            <Tab eventKey="comments" title="Comments">
              <div className="card">
                <div className="card-body">
                  {!isClosed && (
                    <Form onSubmit={handleAddComment} className="mb-4 p-3 bg-light rounded">
                      <Tabs
                        activeKey={newComment.commentType}
                        onSelect={(key) => key && setNewComment({ ...newComment, commentType: key })}
                        className="mb-3"
                      >
                        <Tab eventKey="EMPLOYEE_COMMUNICATION" title="Employee Communication" />
                        {canManage && <Tab eventKey="INTERNAL" title="Internal Comments" />}
                      </Tabs>
                      <Form.Group className="mb-2">
                      <Form.Label className="fw-bold">Your Comment <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={newComment.body}
                        onChange={(e) =>
                          setNewComment({ ...newComment, body: e.target.value })
                        }
                        placeholder={canManage && newComment.commentType === 'INTERNAL' ? 'Write an internal note...' : 'Write a message to HR...'}
                        required
                        className="form-control-lg"
                      />
                    </Form.Group>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={
                        commentLoading ||
                        !newComment.body.trim()
                      }
                      title="Send message"
                      size="lg"
                      className="d-flex align-items-center gap-2"
                    >
                      <span>💬</span> {commentLoading ? 'Adding...' : 'Add Comment'}
                    </Button>
                  </Form>
                  )}
                  {isClosed && (
                    <div className="text-center py-4 text-muted">
                      <div className="fs-1 mb-3">🔒</div>
                      <p>This incident is closed. No further comments can be added.</p>
                    </div>
                  )}

                  <div className="comments-list">
                    {displayedComments.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <div className="fs-1 mb-3">💭</div>
                        <p>{comments.length === 0 ? 'No comments yet' : 'No visible comments'}</p>
                      </div>
                    ) : (
                      displayedComments.map((comment) => (
                        <div key={comment.id} className="comment-item mb-3 p-4 bg-white rounded shadow-sm border">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                {comment.author
                                  ? `${comment.author.firstName[0]}${comment.author.lastName[0]}`
                                  : 'U'}
                              </div>
                              <div>
                                <strong className="d-block">
                                  {comment.author
                                    ? `${comment.author.firstName} ${comment.author.lastName}`
                                    : 'Unknown'}
                                </strong>
                                <small className="text-muted">
                                  {comment.createdAt
                                    ? format(new Date(comment.createdAt), 'MMM dd, yyyy HH:mm')
                                    : ''}
                                </small>
                              </div>
                            </div>
                            <span className={`badge ${comment.commentType === 'INTERNAL' ? 'bg-primary' : 'bg-info'}`}>
                              {comment.commentType === 'INTERNAL' ? '🔒 Internal' : '💬 Communication'}
                            </span>
                          </div>
                          <p className="mb-0 mt-3">{comment.body}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Tab>

            <Tab eventKey="attachments" title="Attachments">
              <div className="card">
                <div className="card-body">
                  {!isClosed && (
                    <div className="mb-3">
                      <label className="form-label">Attachments</label>
                      <input 
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                        type="file" 
                        className="form-control"
                        onChange={handleFileUpload}
                        disabled={uploadLoading || attachments.length >= 5}
                      />
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-muted mb-0 form-text">
                          Supported formats: Images, PDF, Word, Excel, Text files
                        </small>
                        <span className="mb-0 badge bg-info">
                          {attachments.length}/5 files
                        </span>
                      </div>
                      {attachments.length >= 5 && (
                        <div className="text-warning mt-2 small">
                          Maximum of 5 files reached. Delete files to upload more.
                        </div>
                      )}
                      
                      {/* Selected Files List */}
                      {selectedFiles.length > 0 && !uploadLoading && (
                        <div className="attachments-list mt-3">
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="attachment-item d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                            >
                              <div>
                                <strong>{file.name}</strong>
                                <small className="text-muted ms-2">
                                  ({(file.size / 1024).toFixed(2)} KB)
                                </small>
                              </div>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleRemoveSelectedFile(index)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {isClosed && (
                    <div className="mb-3 text-muted">
                      <small>This incident is closed. No further attachments can be uploaded.</small>
                    </div>
                  )}
                  {attachments.length === 0 ? (
                    <p className="text-muted">No attachments yet</p>
                  ) : (
                    <div className="attachments-list">
                      {attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="attachment-item d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                        >
                          <div>
                            <strong>{attachment.fileName}</strong>
                            <small className="text-muted ms-2">
                              ({attachment.fileSizeKb} KB)
                            </small>
                            <br />
                            <small className="text-muted">
                              Uploaded by:{' '}
                              {attachment.uploadedBy
                                ? `${attachment.uploadedBy.firstName} ${attachment.uploadedBy.lastName}`
                                : 'Unknown'}
                            </small>
                          </div>
                          {/* <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              // onClick={() => window.open(attachment.storagePath, '_blank')}
                              onClick={() =>
                                window.open(`${getServerUrl()}/storage${attachment.storagePath}`, '_blank')
                              }
                            >
                              View
                            </Button>

                            {canManage && (
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                              >
                                Delete
                              </Button>
                            )}
                          </div> */}
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              title={attachment.mimeType?.startsWith('image/') ? 'Preview attachment' : 'Download attachment'}
                              onClick={async () => {
                                try {
                                  const blob = await getIncidentAttachmentFile(attachment.id, !attachment.mimeType?.startsWith('image/'))
                                  const url = URL.createObjectURL(blob)
                                  if (attachment.mimeType?.startsWith('image/')) {
                                    window.open(url, '_blank', 'noopener,noreferrer')
                                    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
                                  } else {
                                    const link = document.createElement('a')
                                    link.href = url
                                    link.download = attachment.fileName
                                    document.body.appendChild(link)
                                    link.click()
                                    link.remove()
                                    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
                                  }
                                } catch (openError: any) {
                                  setError(openError.message || 'Unable to retrieve attachment.')
                                }
                              }}
                            >
                              {attachment.mimeType?.startsWith('image/') ? <FaEye /> : 'Download'}
                            </Button>
                            {canDeleteAttachment(attachment) && !isClosed && (
                              <Button
                                size="sm"
                                variant="danger"
                                title="Delete Attachment"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Tab>
            {canViewAudit && (
              <Tab eventKey="audit" title="Audit Trail" onEnter={fetchAuditTrail}>
                <div className="card">
                  <div className="card-body">
                    {auditLogs.length === 0 ? <p className="text-muted mb-0">No audit events recorded.</p> : (
                      <ol className="list-group list-group-numbered">
                        {auditLogs.map((entry) => (
                          <li key={entry.id} className="list-group-item">
                            <div className="d-flex justify-content-between gap-3">
                            <strong>{getAuditEventLabel(entry.eventType)}</strong>
                              <small className="text-muted">{format(new Date(entry.createdAt), 'MMM dd, yyyy HH:mm')}</small>
                            </div>
                            <div>{entry.affectedSection || 'Concern'}</div>
                            <small className="text-muted">{entry.actor ? `${entry.actor.firstName} ${entry.actor.lastName}` : 'System'}</small>
                            {(entry.previousValue != null || entry.newValue != null) && (
                              <div className="small mt-2">
                                {entry.previousValue != null && <div>Previous: {formatAuditValue(entry.previousValue, entry)}</div>}
                                {entry.newValue != null && <div>Updated: {formatAuditValue(entry.newValue, entry)}</div>}
                              </div>
                            )}
                            {entry.comment && <div className="small mt-1">{entry.comment}</div>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              </Tab>
            )}
          </Tabs>
        </Col>

        <Col md={4}>
          <div className="card">
            <div className="card-header">
              <h5>Timeline</h5>
            </div>
            <div className="card-body">
              <div className="timeline-item mb-3">
                <small className="text-muted">
                  {incident.createdAt
                    ? format(new Date(incident.createdAt), 'MMM dd, yyyy HH:mm')
                    : ''}
                </small>
                <p className="mb-0">Incident Created</p>
              </div>
              {incident.updatedAt && incident.updatedAt !== incident.createdAt && (
                <div className="timeline-item mb-3">
                  <small className="text-muted">
                    {format(new Date(incident.updatedAt), 'MMM dd, yyyy HH:mm')}
                  </small>
                  <p className="mb-0">Last Updated</p>
                </div>
              )}
              {incident.closedAt && (
                <div className="timeline-item mb-3">
                  <small className="text-muted">
                    {format(new Date(incident.closedAt), 'MMM dd, yyyy HH:mm')}
                  </small>
                  <p className="mb-0">Incident Closed</p>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Assign Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Incident</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Assign To</Form.Label>
            <Form.Select
              value={assignData.assignedToId}
              onChange={(e) =>
                setAssignData({
                  ...assignData,
                    assignedToId: e.target.value,
                  })
                }
              >
                <option value="">Select User</option>

                {assignableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                    {user.employeeCode ? ` (${user.employeeCode})` : ''}
                    {user.role ? ` - ${user.role}` : ''}
                  </option>
                ))}
              </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Remarks <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={assignData.remarks}
              onChange={(e) =>
                setAssignData({ ...assignData, remarks: e.target.value })
              }
              placeholder="Add assignment remarks..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={assignLoading || !assignData.assignedToId}
          >
            {assignLoading ? 'Assigning...' : 'Assign'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusData.status}
              onChange={(e) => {
                const newStatus = e.target.value
                const allowedStatuses = getAllowedNextStatuses()
                if (!allowedStatuses.includes(newStatus)) {
                  alert(`You cannot select this status. Current status is ${getStatusLabel(incident?.status)}. Only ${allowedStatuses.map(s => getStatusLabel(s)).join(', ')} are allowed.`)
                  return
                }
                setStatusData({ ...statusData, status: newStatus })
              }}
            >
              <option value="">Select Status</option>
              {getAllowedNextStatuses().map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Remark <span className="text-danger" aria-hidden="true">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={statusData.remarks}
              onChange={(e) => setStatusData({ ...statusData, remarks: e.target.value })}
              placeholder="Add status change remarks..."
              required
            />
            {!statusData.remarks.trim() && (
              <Form.Text className="text-danger" role="status">Remark is required to update status.</Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleStatusUpdate} disabled={statusLoading || !statusData.status || !statusData.remarks.trim()}>
            {statusLoading ? 'Updating...' : 'Update Status'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default IncidentDetail
